import type { ReactNode } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import { useEffect, useMemo, useState } from 'react'
import {
  addDays,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { packLanes } from '@/components/calendar/lanes'
import { useDragSelect, type DragPoint } from '@/components/calendar/use-drag-select'
import { getReservationStayDates } from '@/lib/reservation-utils'
import { cn } from '@/lib/utils'

/**
 * The Monday-start month grid, with spans drawn as continuous bars.
 *
 * Extracted from the property page's calendar tab (CAP-378) when the cleaning
 * board needed the same picture for a different set of things (CAP-514): a
 * second copy of the week-splitting, lane-packing and row-height arithmetic is
 * the kind of duplication that drifts silently - one copy gets the overflow fix
 * and the other keeps clipping bars.
 *
 * It owns layout and nothing else. What a bar MEANS - a stay, a block, a
 * cleaning - is entirely the caller's, including its colour and its label.
 */

export interface MonthBar {
  /** Stable across week rows; the grid appends the week index itself. */
  key: string
  /** First day the bar covers. */
  start: Date
  /** Exclusive: the first day it does NOT cover (a stay's check-out). */
  endExclusive: Date
  label: ReactNode
  /**
   * Drawn in place of `label` when the bar is under `SHORT_LABEL_CELLS` wide
   * in a row - a one-night stay's half cell, say, where the full label would
   * truncate to its first letter. The `title` still carries the whole of it.
   */
  shortLabel?: ReactNode
  /** Classes for the bar body - border, background and text colour. */
  className?: string
  title?: string
  ariaLabel?: string
  onClick?: () => void
  testId?: string
  /** Extra `data-*` attributes, for tests and for styling hooks. */
  data?: Record<string, string>
  /**
   * Which band the bar sits in. Every tier-0 bar is packed into lanes first
   * and tier 1 starts below all of them, so two kinds of thing (stays and the
   * cleanings that follow them) stay readable as two bands rather than
   * interleaving wherever a lane happened to be free.
   */
  tier?: number
  /**
   * Opt in to the guest-stay geometry (CAP-817): draw the bar from `start` of
   * the way across its first day to `end` of the way across its
   * `endExclusive` day, instead of whole day columns. Omitted, the bar fills
   * whole columns from `start` up to the day before `endExclusive`.
   *
   * Only a bar's true edges are inset. A continuation into the next week row
   * and an edge clipped by the visible month still run flush to the row or
   * month edge.
   */
  edgeInset?: EdgeInset
  /**
   * Run on into the adjacent month's padding days instead of stopping at the
   * visible month's edge (CAP-1005). For a stay, whose check-in and check-out
   * are facts of the booking, not of the month being viewed; a block or gap is
   * counted per month and stays clipped. Only a bar that touches the visible
   * month is drawn at all, either way.
   */
  continuesPastMonth?: boolean
  /**
   * Drawn for the eye only and hidden from assistive tech: a placeholder band
   * for a layer that is still loading says nothing a reader should hear once
   * per week row (the toolbar's live region says what is loading).
   */
  decorative?: boolean
}

export interface EdgeInset {
  /** Fraction (0-1) into the first day's cell where the bar begins. */
  start: number
  /**
   * Fraction (0-1) into the `endExclusive` day's cell where the bar ends. 0
   * ends the bar flush at the last covered day, with no fragment in the
   * `endExclusive` cell (a gap whose next night is held by a block, not a stay).
   */
  end: number
}

/** A stay begins 75% of the way across its check-in cell... */
export const STAY_START_FRACTION = 0.75
/** ...and ends 25% of the way across its check-out cell, so a same-day turnover leaves a gap. */
export const STAY_END_FRACTION = 0.25
export const STAY_EDGE_INSET: EdgeInset = {
  start: STAY_START_FRACTION,
  end: STAY_END_FRACTION,
}

/** Below this many cells wide, a continuation fragment drops its label. */
const MIN_LABEL_CELLS = 0.5
/** Below this many cells wide, a bar with a `shortLabel` draws it instead of its `label`. */
export const SHORT_LABEL_CELLS = 1.5

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/** Must match the overlay's box model in the render below. */
const LANE_HEIGHT = 22
const LANE_GAP = 4
const OVERLAY_TOP_OFFSET = 22
const OVERLAY_BOTTOM_OFFSET = 4
/** The day-number line of a cell: its 20px circle, below the cell's 4px top padding. */
const DAY_NUMBER_LINE_HEIGHT = 24
/** The default week-row floor; a screen with more to show per day passes a taller `dayMinHeight`. */
const DAY_CELL_MIN_HEIGHT = 56

/**
 * Normalize a date-ish value to its calendar date (midnight local time),
 * dropping any time-of-day component. Stay boundaries are days, not instants -
 * a "2026-03-10T16:00:00Z" check-in still occupies the March 10 cell, and
 * comparing full timestamps against midnight day cells would shift bars by a
 * day depending on time-of-day and zone.
 */
export function toCalendarDate(value: unknown) {
  const datePart = String(value).slice(0, 10)
  return parseISO(datePart)
}

/**
 * Build the Monday-start weeks (each an array of 7 Date objects) that cover
 * the given month, including the leading/trailing days from adjacent months
 * needed to fill the grid.
 */
export function buildMonthWeeks(monthCursor: Date) {
  const firstOfMonth = startOfMonth(monthCursor)
  const lastOfMonth = endOfMonth(monthCursor)
  const gridStart = startOfWeek(firstOfMonth, { weekStartsOn: 1 })
  const gridEnd = endOfWeek(lastOfMonth, { weekStartsOn: 1 })

  const weeks: Date[][] = []
  let cursor = gridStart
  while (cursor <= gridEnd) {
    const week: Date[] = []
    for (let i = 0; i < 7; i++) {
      week.push(addDays(cursor, i))
    }
    weeks.push(week)
    cursor = addDays(cursor, 7)
  }
  return weeks
}

/**
 * Track "today" as state that refreshes at the next local midnight (and every
 * midnight thereafter), instead of a one-time `useMemo`. A page can stay
 * mounted across midnight, and a memo with an empty dependency array would
 * keep highlighting yesterday's cell until the component remounts.
 */
export function useToday() {
  const [today, setToday] = useState(() => new Date())

  useEffect(() => {
    const now = new Date()
    const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0)
    const msUntilMidnight = nextMidnight.getTime() - now.getTime()
    const timer = setTimeout(() => setToday(new Date()), msUntilMidnight)
    return () => clearTimeout(timer)
  }, [today])

  return today
}

/**
 * A reservation, as much of one as any calendar here reads directly. The whole
 * object comes from the untyped API client and the reservation-utils helpers,
 * whose real shape is considerably larger.
 */
export interface CalendarReservation {
  id?: string
  reservation_id?: string
  status?: string | null
  uplisting_status?: string | null
  channel?: string | null
  guest_name?: string | null
  /** Top-level on the property calendar read; the reservation record nests it under `external`. */
  total_payout?: number | null
  check_in?: string | Date | null
  check_out?: string | Date | null
  external?: {
    uplisting?: {
      status?: string | null
      total_payout?: number | null
      check_in?: string | Date | null
      check_out?: string | Date | null
      attributes?: {
        check_in?: string | Date | null
        check_out?: string | Date | null
        start_date?: string | Date | null
        end_date?: string | Date | null
      }
    }
  }
  [key: string]: unknown
}

/**
 * One visually distinct colour per channel (Airbnb / Vrbo / Direct) plus an
 * amber "pending/inquiry" state, per the calendar mock in
 * docs/archive/messages-redesign-plan.md. The shared `getChannelBadgeVariant`
 * (src/lib/format-utils.js) can't provide this - it only has two variants
 * (`default`/`secondary`), which collapses Airbnb and Direct into the same
 * colour and Vrbo/pending/unknown into another - so this is a dedicated
 * mapping for calendars rather than a reuse of that badge helper.
 *
 * It lives here, beside the grid, rather than in either screen that draws
 * stays: the same bar must not mean Airbnb on the property page and "a stay"
 * on the cleaning board.
 */
export const CALENDAR_COLOR_CLASSES: Record<string, string> = {
  airbnb: 'bg-red-500/20 border-red-500 text-red-300',
  vrbo: 'bg-blue-500/20 border-blue-500 text-blue-300',
  direct: 'bg-mint/20 border-mint text-text',
  pending: 'bg-amber/20 border-amber text-amber',
  unknown: 'bg-surface2 border-border text-textMuted',
}

/**
 * Whether a reservation is in a pending/inquiry state, checked the same way
 * the existing `getStatusBadgeVariant`/`isReservationCancelled` helpers check
 * status strings (src/lib/format-utils.js, src/lib/reservation-utils.js):
 * substring-match every status source independently.
 */
function isReservationPending(reservation: CalendarReservation) {
  const statuses = [
    reservation?.status,
    reservation?.uplisting_status,
    reservation?.external?.uplisting?.status,
  ]
  return statuses.some(
    status =>
      typeof status === 'string' &&
      (status.toLowerCase().includes('pending') || status.toLowerCase().includes('inquiry'))
  )
}

/**
 * Map a reservation to a calendar colour key: Airbnb / Vrbo / Direct /
 * pending / unknown. Pending status wins outright (mirrors
 * `getReservationLifecycleStatus`'s cancellation-wins-outright precedent).
 */
export function getCalendarColorKey(reservation: CalendarReservation) {
  if (isReservationPending(reservation)) return 'pending'
  const channel = (reservation?.channel || '').toLowerCase()
  if (channel === 'airbnb' || channel === 'airbnb_official') return 'airbnb'
  if (channel === 'vrbo' || channel === 'home_away') return 'vrbo'
  if (channel === 'direct') return 'direct'
  return 'unknown'
}

/**
 * A reservation's stay dates as real Date objects, or null if either side is
 * missing or unparseable.
 */
export function parseStay(reservation: CalendarReservation) {
  const { checkIn, checkOut } = getReservationStayDates(reservation)
  if (!checkIn || !checkOut) return null
  try {
    const checkInDate = toCalendarDate(checkIn)
    const checkOutDate = toCalendarDate(checkOut)
    if (Number.isNaN(checkInDate.getTime()) || Number.isNaN(checkOutDate.getTime())) return null
    if (checkOutDate <= checkInDate) return null
    return { checkInDate, checkOutDate }
  } catch {
    return null
  }
}

interface PlacedBar {
  bar: MonthBar
  /** CSS grid line the bar's first cell starts on (1-indexed). */
  colStart: number
  /** Exclusive CSS grid line after the bar's last cell. */
  colEnd: number
  /** Fraction of the first cell left empty on its left (0 unless a true stay start). */
  insetStart: number
  /** Fraction of the last cell left empty on its right (0 unless a true stay end). */
  insetEnd: number
  /**
   * A fragment under half a cell - a stay's check-out tail, or a Sunday
   * check-in's first quarter cell - is too narrow for any of its label: it
   * would read "Da..." beside the real bar. The bar's title and aria label
   * still carry the name.
   */
  labelHidden: boolean
  /** Too narrow for the full label, so the bar's `shortLabel` is drawn instead. */
  useShortLabel: boolean
  lane: number
}

/**
 * For a single week row (7 consecutive dates), compute the bars that should
 * render across it: one per bar whose span overlaps at least one day in that
 * week, clipped to the week's boundaries, plus a lane index so overlapping
 * bars stack instead of colliding.
 *
 * Only a bar that touches the visible month (`monthInterval`) is drawn - a
 * night in it, or a stay's check-out morning. Most bars are then clipped to
 * the month, since a block or gap is counted per month and must not show
 * occupancy in the adjacent month's padding days. A stay (a bar with
 * `continuesPastMonth`) instead runs on into those padding days (CAP-1005), so one
 * crossing a month boundary keeps its true start and end rather than stopping
 * flat at the month's edge; a stay entirely in the padding days is the
 * adjacent month's business and is left out.
 *
 * A bar with `edgeInset` also occupies its check-out cell (up to the inset
 * fraction) when that cell is in this row and, for a bar clipped to the month,
 * that cell and the stay's last night are both in the visible month. That
 * includes a check-out on the row's first day, which yields a short fragment
 * from the row edge.
 */
export function computeWeekBars(
  week: Date[],
  bars: MonthBar[],
  monthInterval: { start: Date; end: Date }
) {
  const inVisibleMonth = (day: Date) => day >= monthInterval.start && day <= monthInterval.end
  const placed: PlacedBar[] = []
  for (const bar of bars) {
    const edgeInset = bar.edgeInset
    const drawsCheckout = Boolean(edgeInset && edgeInset.end > 0)
    const lastNight = addDays(bar.endExclusive, -1)
    const touchesMonth =
      (bar.start <= monthInterval.end && lastNight >= monthInterval.start) ||
      (drawsCheckout && bar.continuesPastMonth && inVisibleMonth(bar.endExclusive))
    if (!touchesMonth) continue

    // A day at week[i] belongs to this bar if start <= week[i] < endExclusive,
    // and, for a bar clipped to the month, if it also falls within it.
    let startCol = -1
    let endCol = -1
    let checkoutCol = -1
    for (let i = 0; i < 7; i++) {
      const day = week[i]
      const drawable = bar.continuesPastMonth || inVisibleMonth(day)
      if (drawable && day >= bar.start && day < bar.endExclusive) {
        if (startCol === -1) startCol = i
        endCol = i
      } else if (
        drawsCheckout &&
        isSameDay(day, bar.endExclusive) &&
        (bar.continuesPastMonth || (inVisibleMonth(day) && inVisibleMonth(lastNight)))
      ) {
        checkoutCol = i
      }
    }
    if (startCol === -1 && checkoutCol === -1) continue

    let insetStart = 0
    let insetEnd = 0
    if (edgeInset) {
      if (startCol !== -1 && isSameDay(week[startCol], bar.start)) {
        insetStart = edgeInset.start
      }
      if (checkoutCol !== -1) {
        // A checkout-only fragment (the stay's nights are all in earlier rows)
        // begins at the row edge.
        if (startCol === -1) startCol = checkoutCol
        endCol = checkoutCol
        insetEnd = 1 - edgeInset.end
      }
    }
    const widthCells = endCol + 1 - startCol - insetStart - insetEnd
    placed.push({
      bar,
      colStart: startCol + 1, // CSS grid columns are 1-indexed
      colEnd: endCol + 2, // grid-column end is exclusive
      insetStart,
      insetEnd,
      labelHidden: Boolean(edgeInset) && widthCells < MIN_LABEL_CELLS,
      useShortLabel: bar.shortLabel !== undefined && widthCells < SHORT_LABEL_CELLS,
      lane: 0,
    })
  }

  // Greedy lane assignment so overlapping bars don't collide visually, run per
  // tier so each band's lanes are contiguous and the next band starts below
  // (`packLanes`, shared with the portfolio calendar). Positions are fractional
  // (in cells) so a stay ending 25% into a day and the next starting 75% into
  // it share a lane. The bars are drawn in packing order.
  const left = (item: PlacedBar) => item.colStart - 1 + item.insetStart
  const right = (item: PlacedBar) => item.colEnd - 1 - item.insetEnd
  placed.sort((a, b) => (a.bar.tier ?? 0) - (b.bar.tier ?? 0) || left(a) - left(b))
  const { lanes, laneCount } = packLanes(placed, item => ({
    left: left(item),
    right: right(item),
    tier: item.bar.tier,
  }))
  placed.forEach((item, index) => {
    item.lane = lanes[index]
  })

  return { bars: placed, laneCount }
}

interface MonthGridProps {
  monthCursor: Date
  bars: MonthBar[]
  /**
   * Rendered in the top-right of each in-month day cell - a per-night price,
   * a marker, or nothing. Given the day so the caller decides per cell.
   */
  renderDayCorner?: (day: Date) => ReactNode
  /**
   * The least height of a week row, in px. The grid grows a row past this for
   * its bars, so with a floor that fits the busiest week every row is the same
   * height and the grid does not jump when the month changes. Left out, rows
   * are only as tall as their bars need.
   */
  dayMinHeight?: number
  /**
   * Rendered along the bottom of each in-month day cell, below every band of
   * bars: point markers, say. It sits in the cell's own flow under a spacer as
   * tall as the week's bars, so a footer that wraps (a narrow cell, several
   * markers) makes the row taller instead of running up into a bar.
   */
  renderDayFooter?: (day: Date) => ReactNode
  /**
   * Makes each in-month day number a button that calls this with the day: the
   * property calendar opens the day's list from it. Left out, the number is
   * plain text, as on the cleaning board.
   */
  onSelectDay?: (day: Date) => void
  /**
   * Turns on drag-to-select (CAP-1403): press on a day, drag across others,
   * release. Called with the first and the last dragged day, in calendar
   * order whichever way the pointer travelled; a block's exclusive end is the
   * caller's to derive. A press and release on one day is not a drag and
   * reaches `onSelectDay` as a plain click. Mouse and pen only: a finger
   * keeps scrolling the page.
   */
  onDragSelect?: (first: Date, last: Date) => void
}

const DAY_KEY_FORMAT = 'yyyy-MM-dd'

/**
 * The in-month day cell under a pointer. A bar overlays its cells, so
 * the event's own target can be a bar; where the browser can say what is at the
 * pointer's position, that answers instead.
 */
function dayKeyAt(point: DragPoint): string | null {
  const stack =
    typeof document.elementsFromPoint === 'function'
      ? document.elementsFromPoint(point.clientX, point.clientY)
      : []
  const candidates =
    stack.length > 0 ? stack : point.target instanceof Element ? [point.target] : []
  for (const element of candidates) {
    const cell = element.closest<HTMLElement>('[data-drag-day]')
    if (cell) return cell.dataset.dragDay ?? null
  }
  return null
}

export function MonthGrid({
  monthCursor,
  bars,
  renderDayCorner,
  dayMinHeight = DAY_CELL_MIN_HEIGHT,
  renderDayFooter,
  onSelectDay,
  onDragSelect,
}: MonthGridProps) {
  const today = useToday()

  // Drag-to-select (`useDragSelect`, shared with the portfolio calendar). A
  // grid that changes month or stops accepting drags drops any drag in
  // progress.
  const drag = useDragSelect<string>({
    dayAt: dayKeyAt,
    onSelect: (first, last) => onDragSelect?.(parseISO(first), parseISO(last)),
    scope: `${format(monthCursor, 'yyyy-MM')}:${onDragSelect ? 'on' : 'off'}`,
  })
  const dragRange = drag.range

  const startDrag = (event: ReactPointerEvent<HTMLElement>, dayKey: string) => {
    if (!onDragSelect) return
    // Only a press on the cell or its day number arms a drag, not on a control
    // inside it (an event block).
    const control = (event.target as Element).closest('button')
    if (control && control.getAttribute('data-testid') !== 'calendar-day-number') return
    drag.start(event, dayKey)
  }

  const weeks = useMemo(() => buildMonthWeeks(monthCursor), [monthCursor])

  const monthInterval = useMemo(
    () => ({ start: startOfMonth(monthCursor), end: endOfMonth(monthCursor) }),
    [monthCursor]
  )

  const weekRows = useMemo(
    () => weeks.map(week => ({ week, ...computeWeekBars(week, bars, monthInterval) })),
    [weeks, bars, monthInterval]
  )

  return (
    <div className="rounded-card border border-border overflow-hidden">
      <div className="grid grid-cols-7 border-b border-border bg-surface2">
        {WEEKDAY_LABELS.map(label => (
          <div key={label} className="px-2 py-1.5 text-center text-xs font-medium text-textMuted">
            {label}
          </div>
        ))}
      </div>

      {weekRows.map(({ week, bars: weekBars, laneCount }, weekIndex) => {
        // Undersizing this lets bars overflow into the next week row.
        const barsLayerHeight =
          laneCount > 0 ? laneCount * LANE_HEIGHT + (laneCount - 1) * LANE_GAP : 0
        // Where the footer starts, measured from under the day-number line.
        const footerOffset = Math.max(
          0,
          OVERLAY_TOP_OFFSET + barsLayerHeight - DAY_NUMBER_LINE_HEIGHT
        )
        const rowMinHeight = Math.max(
          dayMinHeight,
          OVERLAY_TOP_OFFSET + barsLayerHeight + OVERLAY_BOTTOM_OFFSET
        )

        return (
          <div
            key={weekIndex}
            data-testid="calendar-week-row"
            className="relative grid grid-cols-7 border-b border-border last:border-b-0"
            style={{ minHeight: `${rowMinHeight}px` }}
          >
            {week.map(day => {
              const inCurrentMonth = isSameMonth(day, monthCursor)
              // Only highlight today when it's in the visible month - a
              // leading/trailing padding cell from an adjacent month (e.g.
              // March 1 shown while viewing February) must stay muted even if
              // it happens to be today's real date.
              const isToday = inCurrentMonth && isSameDay(day, today)
              const dayKey = format(day, DAY_KEY_FORMAT)
              const draggable = Boolean(onDragSelect) && inCurrentMonth
              const inDragRange =
                dragRange !== null && dayKey >= dragRange.first && dayKey <= dragRange.last
              return (
                <div
                  key={day.toISOString()}
                  data-testid="calendar-day-cell"
                  data-drag-day={draggable ? dayKey : undefined}
                  data-in-drag-range={inDragRange ? 'true' : undefined}
                  onPointerDown={draggable ? event => startDrag(event, dayKey) : undefined}
                  className={cn(
                    'flex flex-col border-r border-border last:border-r-0 px-1.5 pt-1 text-xs',
                    !inCurrentMonth && 'bg-surface1/50 text-white/50',
                    inCurrentMonth && 'text-text',
                    draggable && 'select-none',
                    inDragRange && 'bg-mint/[0.14] ring-1 ring-inset ring-mint/70'
                  )}
                >
                  <div className="flex items-baseline justify-between gap-1">
                    {inCurrentMonth && onSelectDay ? (
                      <button
                        type="button"
                        data-testid="calendar-day-number"
                        aria-label={`Open the list for ${format(day, 'MMMM d')}`}
                        onClick={() => onSelectDay(day)}
                        className={cn(
                          'inline-flex h-5 w-5 items-center justify-center rounded-full hover:bg-surface2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint',
                          isToday && 'bg-mint text-white font-semibold hover:bg-mint'
                        )}
                      >
                        {format(day, 'd')}
                      </button>
                    ) : (
                      <span
                        className={cn(
                          'inline-flex h-5 w-5 items-center justify-center rounded-full',
                          isToday && 'bg-mint text-white font-semibold'
                        )}
                      >
                        {format(day, 'd')}
                      </span>
                    )}
                    {inCurrentMonth && renderDayCorner?.(day)}
                  </div>
                  {inCurrentMonth && renderDayFooter && (
                    <>
                      {/* Holds the footer below the lowest bar lane: the bars
                          are absolutely positioned, so only this keeps them
                          out of the cell's flow. */}
                      <div aria-hidden="true" style={{ height: `${footerOffset}px` }} />
                      <div className="mt-auto pb-1 pt-1">{renderDayFooter(day)}</div>
                    </>
                  )}
                </div>
              )
            })}

            {/* Bars overlay layer: absolutely positioned over the day-cell grid
                below the weekday number row, using the same 7-column grid so
                `grid-column` lines up with day cells. Stay bars trim their
                ends with percentage margins (see `edgeInset`). */}
            <div
              className="pointer-events-none absolute inset-x-0 bottom-1 grid grid-cols-7 content-start gap-y-1"
              style={{ top: `${OVERLAY_TOP_OFFSET}px` }}
            >
              {weekBars.map(
                ({
                  bar,
                  colStart,
                  colEnd,
                  insetStart,
                  insetEnd,
                  labelHidden,
                  useShortLabel,
                  lane,
                }) => {
                  // The bar spans whole grid cells; an inset is trimmed off the
                  // ends with percentage margins. A grid item's percentage
                  // margin resolves against its grid area, so express the
                  // fraction of a cell as a fraction of the whole span.
                  const spanCells = colEnd - colStart
                  const margins =
                    insetStart || insetEnd
                      ? {
                          marginLeft: `${(insetStart / spanCells) * 100}%`,
                          marginRight: `${(insetEnd / spanCells) * 100}%`,
                        }
                      : {}
                  const label = labelHidden ? null : useShortLabel ? bar.shortLabel : bar.label
                  const shared = {
                    'data-testid': bar.testId,
                    ...(bar.data ?? {}),
                    className: cn(
                      'pointer-events-auto truncate rounded border px-1.5 text-left text-[11px] leading-[20px]',
                      bar.className
                    ),
                    // A fixed height, not the label's line box: a bar whose
                    // label is hidden (a check-out stub) would otherwise
                    // collapse to its border when no other bar shares the row.
                    style: {
                      gridColumn: `${colStart} / ${colEnd}`,
                      gridRow: lane + 1,
                      height: `${LANE_HEIGHT}px`,
                      ...margins,
                    },
                    title: bar.title,
                    ...(bar.decorative ? { 'aria-hidden': true } : {}),
                  }
                  // A bar with nothing to open is a label, not a control. A
                  // disabled button would still take a tab stop's worth of
                  // reader attention and announce as unavailable, which is not
                  // what "this week is booked" means.
                  return bar.onClick ? (
                    <button
                      key={`${bar.key}-${weekIndex}`}
                      type="button"
                      aria-label={bar.ariaLabel}
                      onClick={bar.onClick}
                      {...shared}
                    >
                      {label}
                    </button>
                  ) : (
                    <div key={`${bar.key}-${weekIndex}`} aria-label={bar.ariaLabel} {...shared}>
                      {label}
                    </div>
                  )
                }
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
